import { copy } from '../copy'
import { useAuthStore } from '../store/auth'

export class ApiError extends Error {
  readonly status: number
  readonly isStructured: boolean
  readonly code?: string

  constructor(
    status: number,
    message: string,
    details?: { isStructured?: boolean; code?: string },
  ) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.isStructured = details?.isStructured ?? false
    this.code = details?.code
  }
}

/** HTTP metadata for idempotent mutations and operations whose committed state
 * may still need convergence. Existing API methods return `data` directly. */
export interface ApiResponse<T> {
  data: T
  status: number
  retryAfterSeconds: number | null
}

/** The request never reached the server (offline, DNS, CORS, timeout). */
export class NetworkError extends Error {
  constructor(cause?: unknown) {
    super(copy.net.unreachable)
    this.name = 'NetworkError'
    if (cause !== undefined) this.cause = cause
  }
}

/**
 * Extract a human-readable message from a non-2xx response. Handles the JSON
 * error shapes the API uses (`{ error: { message } }`, `{ message }`,
 * `{ error: "..." }`) and falls back to plain text. Raw HTML error pages (e.g.
 * a proxy/nginx 5xx) are suppressed in favor of a generic message so users
 * never see markup.
 */
interface ParsedError {
  message: string
  isStructured: boolean
  code?: string
}

async function parseError(res: Response): Promise<ParsedError> {
  const fallback = copy.net.requestFailed(res.status)
  const contentType = res.headers.get('content-type') ?? ''
  try {
    if (isJsonContentType(contentType)) {
      const data = (await res.json()) as Record<string, unknown>
      const nestedError =
        data.error && typeof data.error === 'object'
          ? (data.error as Record<string, unknown>)
          : null
      const msg =
        nestedError?.message ??
        data.message ??
        (typeof data.error === 'string' ? data.error : null)
      const code = nestedError?.code ?? data.code
      const isStructured = typeof msg === 'string' && !!msg.trim()
      return {
        message: isStructured ? msg : fallback,
        isStructured,
        code: typeof code === 'string' ? code : undefined,
      }
    }
    const text = (await res.text()).trim()
    return {
      message: !text || text.startsWith('<') ? fallback : text,
      isStructured: false,
    }
  } catch {
    return { message: fallback, isStructured: false }
  }
}

function isJsonContentType(value: string): boolean {
  const mediaType = value.split(';', 1)[0].trim().toLowerCase()
  return mediaType === 'application/json' || mediaType.endsWith('+json')
}

function retryAfterSeconds(res: Response): number | null {
  const value = res.headers.get('retry-after')?.trim()
  if (!value) return null
  if (/^\d+$/.test(value)) {
    const seconds = Number(value)
    return Number.isSafeInteger(seconds) ? seconds : null
  }
  const at = Date.parse(value)
  if (Number.isNaN(at)) return null
  return Math.max(0, Math.ceil((at - Date.now()) / 1000))
}

async function requestWithMeta<T>(
  path: string,
  init?: RequestInit,
  isRetry = false,
): Promise<ApiResponse<T>> {
  const token = useAuthStore.getState().token
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(init?.headers as Record<string, string>),
  }
  if (token) headers.Authorization = `Bearer ${token}`

  let res: Response
  try {
    res = await fetch(path, { ...init, headers })
  } catch (cause) {
    throw new NetworkError(cause)
  }

  if (res.status === 401) {
    if (!isRetry) {
      try {
        await useAuthStore.getState().refreshToken()
        return requestWithMeta<T>(path, init, true)
      } catch {
        // refresh failed — fall through to logout
      }
    }
    useAuthStore.getState().logout()
    throw new ApiError(401, copy.net.unauthorized)
  }
  if (!res.ok) {
    const parsed = await parseError(res)
    throw new ApiError(res.status, parsed.message, {
      isStructured: parsed.isStructured,
      code: parsed.code,
    })
  }
  const data =
    res.status === 204 ||
    !isJsonContentType(res.headers.get('content-type') ?? '')
      ? (undefined as T)
      : ((await res.json()) as T)
  return {
    data,
    status: res.status,
    retryAfterSeconds: retryAfterSeconds(res),
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  return (await requestWithMeta<T>(path, init)).data
}

export const api = {
  get: <T>(path: string, init?: RequestInit) => request<T>(path, init),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: 'POST',
      body: body != null ? JSON.stringify(body) : undefined,
    }),
  postWithMeta: <T>(path: string, body?: unknown) =>
    requestWithMeta<T>(path, {
      method: 'POST',
      body: body != null ? JSON.stringify(body) : undefined,
    }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: 'PUT',
      body: body != null ? JSON.stringify(body) : undefined,
    }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: 'PATCH',
      body: body != null ? JSON.stringify(body) : undefined,
    }),
  patchWithMeta: <T>(path: string, body?: unknown) =>
    requestWithMeta<T>(path, {
      method: 'PATCH',
      body: body != null ? JSON.stringify(body) : undefined,
    }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  deleteWithMeta: <T>(path: string) =>
    requestWithMeta<T>(path, { method: 'DELETE' }),
}
